import React, { useEffect, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import { FaDownload, FaChevronLeft, FaChevronRight, FaTimes, FaSpinner } from 'react-icons/fa';
import MobilePDFViewer from './MobilePDFViewer';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PageFlipViewer = ({ pdfUrl, title = "PDF Document", onClose }) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device - must be called before any conditional returns
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use mobile viewer for mobile devices
  if (isMobile) {
    return <MobilePDFViewer pdfUrl={pdfUrl} title={title} onClose={onClose} />;
  }

  // Desktop viewer component (flip book)
  return <DesktopFlipBookViewer pdfUrl={pdfUrl} title={title} onClose={onClose} />;
};

// Separate component for desktop flip book viewer
const DesktopFlipBookViewer = ({ pdfUrl, title, onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dimensions, setDimensions] = useState({ width: 600, height: 850, minWidth: 280, minHeight: 400 });
  const flipBookRef = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  // Convert PDF pages to canvas images for flipbook
  const renderPageToCanvas = async (page, canvas, targetWidth, targetHeight) => {
    const context = canvas.getContext('2d');
    
    // Calculate scale to fit target dimensions
    const viewport = page.getViewport({ scale: 1.0 });
    const scale = Math.min(targetWidth / viewport.width, targetHeight / viewport.height);
    const scaledViewport = page.getViewport({ scale: scale * 2 }); // 2x for high DPI
    
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    
    const renderContext = {
      canvasContext: context,
      viewport: scaledViewport,
    };
    
    await page.render(renderContext).promise;
    return canvas.toDataURL('image/png');
  };

  // Load PDF and convert pages to images
  useEffect(() => {
    if (!pdfUrl) return;

    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const loadingTask = pdfjs.getDocument({
          url: pdfUrl,
          cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
          cMapPacked: true,
        }).promise;
        
        const pdf = await loadingTask;
        const totalPages = pdf.numPages;
        setNumPages(totalPages);

        const images = [];
        const defaultWidth = 600;
        const defaultHeight = 850;

        // Load all pages
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const canvas = document.createElement('canvas');
          const imageUrl = await renderPageToCanvas(page, canvas, defaultWidth, defaultHeight);
          images.push(imageUrl);
        }

        setPageImages(images);
        setCurrentPage(1);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError('Failed to load PDF document. Please try again or download the file.');
        setLoading(false);
      }
    };

    loadPDF();
  }, [pdfUrl]);

  // Calculate responsive dimensions based on actual container size
  const calculateDimensions = () => {
    if (typeof window === 'undefined') {
      return { width: 600, height: 850, minWidth: 280, minHeight: 400 };
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth <= 640;
    const isTablet = viewportWidth > 640 && viewportWidth <= 1024;
    
    // Get actual container size if available, otherwise use viewport
    let availableWidth = viewportWidth;
    let availableHeight = viewportHeight;
    
    if (contentRef.current) {
      const contentRect = contentRef.current.getBoundingClientRect();
      if (contentRect.width > 0 && contentRect.height > 0) {
        availableWidth = contentRect.width;
        availableHeight = contentRect.height;
      }
    }
    
    // Account for padding and UI elements
    const padding = isMobile ? 16 : 32; // Less padding on mobile
    const headerHeight = isMobile ? 60 : 70; // Header height
    const navButtonSpace = isMobile ? 0 : 100; // Space for side nav buttons on desktop
    const pageCounterSpace = 80; // Space for page counter at bottom
    
    // Calculate available space for flipbook
    // On desktop, we need space for TWO pages side by side
    const spaceForFlipbook = availableWidth - padding * 2 - navButtonSpace;
    const spaceForHeight = availableHeight - headerHeight - padding * 2 - pageCounterSpace;
    
    // Maintain aspect ratio (A4 is approximately 210:297 = 1:1.414)
    const aspectRatio = 1.414;
    
    let singlePageWidth, height, maxWidth;
    
    if (isMobile) {
      // Mobile: show single page
      const maxWidthForSingle = Math.max(250, spaceForFlipbook);
      const maxHeight = Math.max(350, spaceForHeight);
      
      singlePageWidth = maxWidthForSingle;
      height = singlePageWidth * aspectRatio;
      
      // If height is too large, constrain by height instead
      if (height > maxHeight) {
        height = maxHeight;
        singlePageWidth = height / aspectRatio;
      }
      
      maxWidth = Math.round(maxWidthForSingle);
    } else {
      // Desktop/Tablet: show two pages side by side
      // react-pageflip shows two pages side by side automatically
      // We need to ensure single page width allows both pages to fit
      const maxHeight = Math.max(400, spaceForHeight);
      
      // Calculate based on height first (most common constraint)
      const heightBasedSinglePageWidth = maxHeight / aspectRatio;
      const heightBasedTotalWidth = heightBasedSinglePageWidth * 2; // Two pages side by side
      
      // Check if width allows for this
      if (heightBasedTotalWidth <= spaceForFlipbook) {
        // Height is the limiting factor - use it
        height = maxHeight;
        singlePageWidth = heightBasedSinglePageWidth;
        maxWidth = spaceForFlipbook; // Allow full available width for two pages
      } else {
        // Width is the limiting factor
        // Calculate single page width from available width
        // react-pageflip will show two pages, so we need: 2 * pageWidth <= availableWidth
        singlePageWidth = spaceForFlipbook / 2.1; // Divide by slightly more than 2 to account for gap/spine
        height = singlePageWidth * aspectRatio;
        maxWidth = spaceForFlipbook;
        
        // If this makes height too large, we need to constrain by height
        if (height > maxHeight) {
          height = maxHeight;
          singlePageWidth = height / aspectRatio;
          // Recalculate maxWidth to allow two pages
          maxWidth = Math.min(spaceForFlipbook, singlePageWidth * 2.1);
        }
      }
      
      // Ensure minimum width for single page
      const minSinglePageWidth = isTablet ? 250 : 300;
      if (singlePageWidth < minSinglePageWidth) {
        singlePageWidth = minSinglePageWidth;
        height = singlePageWidth * aspectRatio;
        maxWidth = Math.max(spaceForFlipbook, singlePageWidth * 2.1);
      }
      
      // CRITICAL: maxWidth must be at least 2x single page width to allow two pages to show
      // Add extra space for gap/spine between pages
      maxWidth = Math.max(maxWidth, singlePageWidth * 2.2);
    }
    
    // Ensure minimum dimensions
    singlePageWidth = Math.max(isMobile ? 250 : 300, Math.round(singlePageWidth));
    height = Math.max(isMobile ? 350 : 400, Math.round(height));
    
    return {
      width: singlePageWidth, // Width of a single page (library shows two side by side on desktop)
      height: height,
      minWidth: isMobile ? 250 : 300,
      minHeight: isMobile ? 350 : 400,
      maxWidth: Math.round(maxWidth), // Must allow space for TWO pages side by side (2x + gap)
      maxHeight: Math.round(spaceForHeight),
    };
  };

  // Update dimensions when window resizes or content is ready
  useEffect(() => {
    if (loading || pageImages.length === 0) return;

    const updateDimensions = () => {
      // Use a small delay to ensure DOM is ready
      setTimeout(() => {
        const newDims = calculateDimensions();
        setDimensions(newDims);
      }, 100);
    };

    // Initial calculation after a short delay to ensure DOM is rendered
    const timeoutId = setTimeout(updateDimensions, 100);

    // Recalculate on window resize
    const handleResize = () => {
      clearTimeout(timeoutId);
      updateDimensions();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Use ResizeObserver if available
    let resizeObserver = null;
    if (contentRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [loading, pageImages.length]);

  const handleDownload = async () => {
    try {
      // Fetch the PDF as a blob to force download
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Set download attribute with sanitized filename
      const sanitizedTitle = (title || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.download = `${sanitizedTitle}.pdf`;
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback: open in new tab if download fails
      window.open(pdfUrl, '_blank');
    }
  };

  const goToPrevPage = () => {
    if (currentPage <= 1) return;
    if (flipBookRef.current && flipBookRef.current.pageFlip) {
      const pageFlip = flipBookRef.current.pageFlip();
      const currentIndex = pageFlip.getCurrentPageIndex();
      if (currentIndex > 0) {
        pageFlip.flipPrev();
      }
    }
  };

  const goToNextPage = () => {
    if (currentPage >= numPages) return;
    if (flipBookRef.current && flipBookRef.current.pageFlip) {
      const pageFlip = flipBookRef.current.pageFlip();
      const currentIndex = pageFlip.getCurrentPageIndex();
      if (currentIndex < numPages - 1) {
        pageFlip.flipNext();
      }
    }
  };

  // Add responsive styles
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const styleId = 'page-flip-viewer-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    // Use Cloudinary URL for background image (CDN)
    const bgImagePath = 'https://res.cloudinary.com/dsoekzozv/image/upload/v1768652336/beige-wooden-textured-flooring-background-min_fy26bs.jpg';
    
    style.textContent = `
      .page-flip-viewer-container {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.9);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        overflow: hidden;
      }

      .page-flip-viewer-wrapper {
        background: white;
        border-radius: 0.5rem;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        max-width: 100%;
        max-height: 100%;
        width: 100%;
        height: 100%;
      }

      .page-flip-viewer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem;
        border-bottom: 1px solid #e5e7eb;
        flex-shrink: 0;
        background: #f9fafb;
        border-radius: 0.5rem 0.5rem 0 0;
      }

      .page-flip-viewer-title {
        font-size: 1.125rem;
        font-weight: 600;
        color: #1f2937;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        margin-right: 1rem;
      }

      .page-flip-viewer-content {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background-image: url('${bgImagePath}');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-color: #d4c5b0;
        position: relative;
        overflow: visible;
        min-height: 0;
        max-height: 100%;
        box-sizing: border-box;
      }

      @media (max-width: 640px) {
        .page-flip-viewer-content {
          overflow: hidden;
        }
      }

      .page-flip-viewer-flipbook-wrapper {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        position: relative;
        overflow: visible;
        box-sizing: border-box;
      }

      .page-flip-book {
        max-width: 100% !important;
        max-height: 100% !important;
        width: auto !important;
        height: auto !important;
      }

      /* Ensure flipbook shows two pages side by side on desktop */
      @media (min-width: 641px) {
        .page-flip-viewer-flipbook-wrapper {
          overflow: visible !important;
        }
        
        .page-flip-viewer-content {
          overflow: visible !important;
        }

        .page-flip-book {
          width: auto !important;
        }
      }

      .page-flip-nav-button {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;
        background: white;
        border: none;
        border-radius: 0.5rem;
        padding: 0.75rem;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
      }

      .page-flip-nav-button:hover:not(:disabled) {
        background: #f3f4f6;
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
      }

      .page-flip-nav-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .page-flip-nav-button-prev {
        left: 1rem;
      }

      .page-flip-nav-button-next {
        right: 1rem;
      }

      .page-flip-mobile-nav {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        gap: 0.5rem;
        z-index: 10;
      }

      .page-flip-page-counter {
        position: absolute;
        bottom: 1rem;
        left: 50%;
        transform: translateX(-50%);
        background: white;
        padding: 0.5rem 1rem;
        border-radius: 9999px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
        z-index: 10;
      }

      /* Responsive styles */
      @media (max-width: 640px) {
        .page-flip-viewer-container {
          padding: 0;
        }

        .page-flip-viewer-wrapper {
          border-radius: 0;
          height: 100vh;
          max-height: 100vh;
        }

        .page-flip-viewer-header {
          padding: 0.75rem;
          border-radius: 0;
          flex-shrink: 0;
        }

        .page-flip-viewer-title {
          font-size: 1rem;
        }

        .page-flip-nav-button-prev,
        .page-flip-nav-button-next {
          display: none;
        }

        .page-flip-mobile-nav {
          display: flex;
        }

        .page-flip-viewer-content {
          padding: 0.5rem;
          min-height: 0;
          flex: 1 1 auto;
          overflow: hidden;
        }

        .page-flip-viewer-flipbook-wrapper {
          padding: 0.5rem;
          min-width: 0;
          min-height: 0;
        }

        .page-flip-book {
          max-width: calc(100vw - 1rem) !important;
          max-height: calc(100vh - 180px) !important;
        }
      }

      @media (min-width: 641px) {
        .page-flip-mobile-nav {
          display: none;
        }

        .page-flip-nav-button-prev,
        .page-flip-nav-button-next {
          display: flex;
        }
      }

      /* Hide scrollbars */
      .page-flip-viewer-container * {
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .page-flip-viewer-container *::-webkit-scrollbar {
        display: none;
        width: 0;
        height: 0;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= numPages;

  if (error) {
    return (
      <div className="page-flip-viewer-container">
        <div className="page-flip-viewer-wrapper" style={{ maxWidth: '500px', height: 'auto' }}>
          <div className="page-flip-viewer-header">
            <h3 className="page-flip-viewer-title">{title}</h3>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-2"
                title="Close"
              >
                <FaTimes size={20} />
              </button>
            )}
          </div>
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{error}</p>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <FaDownload className="mr-2" />
              Download PDF
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-flip-viewer-container" ref={containerRef}>
      <div className="page-flip-viewer-wrapper">
        {/* Header */}
        <div className="page-flip-viewer-header">
          <h3 className="page-flip-viewer-title">{title}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              title="Download PDF"
            >
              <FaDownload style={{ marginRight: '0.25rem' }} />
              <span className="hidden sm:inline">Download</span>
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                title="Close"
              >
                <FaTimes size={20} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="page-flip-viewer-content" ref={contentRef}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <FaSpinner className="animate-spin text-blue-600" size={32} />
              <p style={{ color: '#4b5563' }}>Loading PDF pages...</p>
            </div>
          ) : pageImages.length > 0 ? (
            <div className="page-flip-viewer-flipbook-wrapper">
              {/* Desktop Navigation Buttons */}
              <button
                onClick={goToPrevPage}
                disabled={isFirstPage}
                className={`page-flip-nav-button page-flip-nav-button-prev ${isFirstPage ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Previous Page"
              >
                <FaChevronLeft size={24} style={{ color: isFirstPage ? '#9ca3af' : '#374151' }} />
              </button>

              {/* Flipbook */}
              <HTMLFlipBook
                ref={flipBookRef}
                width={dimensions.width}
                height={dimensions.height}
                minWidth={dimensions.minWidth}
                maxWidth={dimensions.maxWidth}
                minHeight={dimensions.minHeight}
                maxHeight={dimensions.maxHeight}
                size="stretch"
                maxShadowOpacity={0}
                showCover={true}
                usePortrait={false}
                mobileScrollSupport={true}
                className="page-flip-book"
                style={{
                  margin: '0 auto',
                  display: 'block',
                  width: 'auto',
                  height: 'auto',
                  maxWidth: `${dimensions.maxWidth}px`, // Explicitly allow space for two pages
                }}
                onFlip={(e) => {
                  // e.data is 0-indexed page number (the left page in the spread)
                  setCurrentPage(e.data + 1);
                }}
              >
                {pageImages.map((imageUrl, index) => (
                  <div
                    key={index}
                    className="page"
                    data-density="hard"
                    style={{
                      backgroundColor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0',
                      boxShadow: 'none',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Page ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                      draggable={false}
                    />
                  </div>
                ))}
              </HTMLFlipBook>

              {/* Desktop Navigation Button Next */}
              <button
                onClick={goToNextPage}
                disabled={isLastPage}
                className={`page-flip-nav-button page-flip-nav-button-next ${isLastPage ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Next Page"
              >
                <FaChevronRight size={24} style={{ color: isLastPage ? '#9ca3af' : '#374151' }} />
              </button>

              {/* Mobile Navigation Buttons */}
              <div className="page-flip-mobile-nav">
                <button
                  onClick={goToPrevPage}
                  disabled={isFirstPage}
                  className={`page-flip-nav-button ${isFirstPage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Previous Page"
                  style={{ position: 'static', transform: 'none' }}
                >
                  <FaChevronLeft size={20} style={{ color: isFirstPage ? '#9ca3af' : '#374151' }} />
                </button>
                <button
                  onClick={goToNextPage}
                  disabled={isLastPage}
                  className={`page-flip-nav-button ${isLastPage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Next Page"
                  style={{ position: 'static', transform: 'none' }}
                >
                  <FaChevronRight size={20} style={{ color: isLastPage ? '#9ca3af' : '#374151' }} />
                </button>
              </div>

              {/* Page Counter */}
              {numPages && (
                <div className="page-flip-page-counter">
                  Page {currentPage} of {numPages}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PageFlipViewer;
